// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * Wiring. The two workers, the four stages, and the flow control between them.
 *
 * Nothing heavy happens here: the decoder lives in scan.worker, both model
 * towers live in embed.worker, and this file owns the index, the state machine
 * and the DOM.
 */

import './styles/main.css';
import { ABOUT, HOW_IT_WORKS, LIMITS, PRIVACY } from './copy.ts';
import { bindLogToggle, logEvent, logText } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import { openModal } from './modal.ts';
import {
  buildManifest, chaptersVtt, clipName, cuepointsCsv, safeName, type QueryResult,
} from './core/artefacts.ts';
import { findMoments, medianFilter } from './core/moments.ts';
import { FrameIndex } from './core/score.ts';
import { RATES, buildLadder, estimateSeconds, formatDuration } from './core/sampling.ts';
import {
  MODEL_ID, MODEL_REVISION, TOTAL_BYTES, TOTAL_BYTES_WASM, VISION_DTYPE,
} from './model/config.mjs';
import {
  clearError, el, renderFacts, renderResults, renderShell, showError, type ResultView,
} from './ui.ts';
import { DEFAULT_SETTINGS, type Moment, type MomentSettings, type VideoFacts } from './types.ts';
import type { Device } from './model/embedder.ts';
import type { ScanRequest, ScanResponse } from './workers/scan.worker.ts';
import type { EmbedRequest, EmbedResponse } from './workers/embed.worker.ts';

type Stage = 'drop' | 'facts' | 'scan' | 'search';

interface State {
  facts: VideoFacts | null;
  rate: number;
  index: FrameIndex | null;
  planned: number;
  scores: Float32Array | null;
  query: string;
  moments: Moment[];
  settings: MomentSettings;
  thumbs: Map<number, ImageBitmap>;
  modelReady: boolean;
  visionDtype: string;
  scanning: boolean;
  results: QueryResult[];
}

const state: State = {
  facts: null,
  rate: 1,
  index: null,
  planned: 0,
  scores: null,
  query: '',
  moments: [],
  settings: { ...DEFAULT_SETTINGS },
  thumbs: new Map(),
  modelReady: false,
  visionDtype: VISION_DTYPE,
  scanning: false,
  results: [],
};

let scanWorker: Worker | null = null;
let embedWorker: Worker | null = null;
let scanStarted = 0;
let scanDone = 0;
let queryId = 0;
let pendingQuery: ((v: Float32Array) => void) | null = null;
let clipId = 0;
const pendingClips = new Map<number, (bytes: ArrayBuffer) => void>();

const send = (m: ScanRequest) => scanWorker?.postMessage(m);
const sendEmbed = (m: EmbedRequest) => embedWorker?.postMessage(m);

function setStage(stage: Stage): void {
  el('stage-drop').hidden = stage !== 'drop';
  el('stage-facts').hidden = stage !== 'facts';
  el('stage-scan').hidden = stage !== 'scan';
  el('stage-search').hidden = stage !== 'search';
}

/* ── workers ─────────────────────────────────────────────────────────── */

function startWorkers(): void {
  scanWorker?.terminate();
  embedWorker?.terminate();
  scanWorker = new Worker(new URL('./workers/scan.worker.ts', import.meta.url), { type: 'module' });
  embedWorker = new Worker(new URL('./workers/embed.worker.ts', import.meta.url), { type: 'module' });
  scanWorker.onmessage = (e: MessageEvent<ScanResponse>) => onScanMessage(e.data);
  embedWorker.onmessage = (e: MessageEvent<EmbedResponse>) => onEmbedMessage(e.data);
  const fail = (what: string) => () => showError(`The ${what} stopped unexpectedly. Reload the page and try again.`);
  scanWorker.onerror = fail('video reader');
  embedWorker.onerror = fail('model');
}

function onScanMessage(msg: ScanResponse): void {
  switch (msg.kind) {
    case 'facts': {
      state.facts = msg.facts;
      if (!msg.facts.decodable) {
        showError(
          `This browser cannot decode ${msg.facts.codec}. Chrome, Edge and Safari 16.4 or later handle H.264 MP4 files, which is what most cameras record.`,
        );
        setStage('drop');
        return;
      }
      logEvent(
        'good',
        `Opened ${msg.facts.name}: ${formatDuration(msg.facts.duration)}, ${msg.facts.width}×${msg.facts.height}, ${msg.facts.codec}, ${msg.facts.keyframes.length} keyframes.`,
      );
      showFacts();
      return;
    }
    case 'plan': {
      state.planned = msg.times.length;
      state.index = new FrameIndex(msg.times.length);
      logEvent(
        'info',
        `Sampling ${msg.times.length} frames (${msg.onKeyframe} land on keyframes${
          msg.keyframeSpacing ? `, one every ${msg.keyframeSpacing.toFixed(1)} s` : ''
        }).`,
      );
      return;
    }
    case 'frame':
      sendEmbed({ kind: 'frame', index: msg.index, time: msg.time, rgba: msg.rgba });
      return;
    case 'thumb': {
      state.thumbs.set(Math.round(msg.time * 1000), msg.bitmap);
      paintResults();
      return;
    }
    case 'done':
      finishScan();
      return;
    case 'clip': {
      pendingClips.get(msg.id)?.(msg.bytes);
      pendingClips.delete(msg.id);
      return;
    }
    case 'clipProgress':
      return;
    case 'log':
      logEvent(msg.level, msg.message);
      return;
    case 'error':
      state.scanning = false;
      logEvent('error', msg.message);
      showError(msg.message);
      return;
  }
}

function onEmbedMessage(msg: EmbedResponse): void {
  switch (msg.kind) {
    case 'progress': {
      // A download that reaches 100% is followed by graph compilation, which on
      // a slow machine is tens of seconds with nothing to report. Saying
      // "Downloading … 100%" through all of it looks like a hang, so the phase
      // changes as soon as the bytes are in.
      const pct = msg.total ? Math.round((msg.loaded / msg.total) * 100) : 0;
      const phase =
        msg.stage === 'checking the model'
          ? 'Checking the model computes what it should'
          : pct >= 100
            ? `Preparing ${msg.stage}…`
            : `Downloading ${msg.stage}`;
      setProgress(phase, pct / 100, pct >= 100 ? '' : `${pct}%`, '', '');
      return;
    }
    case 'ready': {
      state.modelReady = true;
      const r = msg.report;
      state.visionDtype = r.visionDtype;
      logEvent(
        'good',
        `Model ready on ${r.device === 'webgpu' ? 'WebGPU' : `WebAssembly (${r.threads} thread${r.threads === 1 ? '' : 's'})`}. ` +
          `Self-check passed: image ${r.imageCosine.toFixed(4)}, text ${r.textCosine.toFixed(4)}.`,
      );
      for (const why of r.rejected) logEvent('warn', why);
      if (state.scanning) send({ kind: 'scan', rate: state.rate });
      return;
    }
    case 'vector': {
      // A credit returns to the decoder for every frame that finishes, which is
      // what stops a fast decoder from queueing gigabytes of pixels.
      send({ kind: 'ack' });
      state.index?.push(msg.time, new Float32Array(msg.vector));
      scanDone += 1;
      reportScanProgress();
      return;
    }
    case 'query': {
      pendingQuery?.(new Float32Array(msg.vector));
      pendingQuery = null;
      return;
    }
    case 'log':
      logEvent(msg.level, msg.message);
      return;
    case 'error':
      // Stop the decoder as well. It is running on credit returned per embedded
      // frame, so a failure that ends the inference queue would otherwise leave
      // it waiting for an acknowledgement that is never coming — a silent stall
      // behind a visible error, which is the worst of both.
      send({ kind: 'cancel' });
      state.scanning = false;
      logEvent('error', msg.message);
      showError(msg.message);
      return;
  }
}

/* ── stages ──────────────────────────────────────────────────────────── */

async function openFile(file: File): Promise<void> {
  clearError();
  state.facts = null;
  state.index = null;
  state.scores = null;
  state.moments = [];
  state.results = [];
  state.thumbs.forEach((b) => b.close());
  state.thumbs.clear();
  startWorkers();
  logEvent('info', `Reading ${file.name} (${(file.size / 1e6).toFixed(1)} MB)…`);
  send({ kind: 'open', file });
}

function showFacts(): void {
  const facts = state.facts;
  if (!facts) return;
  const plan = planFor(state.rate);
  const modelBytes = state.modelReady ? 0 : 'gpu' in navigator ? TOTAL_BYTES : TOTAL_BYTES_WASM;
  el('stage-facts').innerHTML = renderFacts(facts, plan, modelBytes, state.rate);
  setStage('facts');

  const rate = el<HTMLSelectElement>('rate');
  rate.addEventListener('change', () => {
    const next = Number(rate.value);
    state.rate = (RATES as readonly number[]).includes(next) ? next : 1;
    showFacts();
  });
  el('start-scan').addEventListener('click', () => void beginScan());
  el('pick-other').addEventListener('click', () => {
    setStage('drop');
    el<HTMLInputElement>('file-input').value = '';
  });
  updateEta(plan.frames);
}

/**
 * Frame count for a rate, computed here so changing the picker is instant.
 *
 * The worker builds the authoritative ladder when the scan actually starts;
 * this calls the SAME function rather than approximating it, so the number the
 * user agreed to and the number of frames that get looked at cannot drift
 * apart.
 */
function planFor(rate: number): { frames: number; onKeyframe: number; keyframeSpacing: number | null } {
  const facts = state.facts;
  if (!facts) return { frames: 0, onKeyframe: 0, keyframeSpacing: null };
  const ladder = buildLadder(facts.keyframes, facts.duration, rate);
  return { frames: ladder.times.length, onKeyframe: ladder.onKeyframe, keyframeSpacing: ladder.keyframeSpacing };
}

/**
 * The estimate shown before the scan starts.
 *
 * A firm number is given only for the WebAssembly tier, where it was measured
 * (roughly a quarter of a second a frame on a recent laptop, single-threaded,
 * because a static host cannot enable cross-origin isolation and therefore
 * cannot have threads). For WebGPU no number is quoted at all, because none has
 * been measured on this machine — the real rate replaces this line a few
 * seconds into the scan either way.
 */
function updateEta(frames: number): void {
  const line = document.getElementById('plan-eta');
  if (!line) return;
  const hasGpu = 'gpu' in navigator;
  if (hasGpu) {
    line.innerHTML = `This browser has WebGPU, so the scan should be quick — the real speed is measured and shown a few seconds in.`;
  } else {
    const seconds = estimateSeconds(frames, 4);
    line.innerHTML = `No WebGPU here, so this runs on a single processor core: roughly <strong>${
      seconds ? formatDuration(seconds) : '—'
    }</strong>. The real speed is measured and shown a few seconds in.`;
  }
}

async function beginScan(): Promise<void> {
  clearError();
  state.scanning = true;
  scanStarted = performance.now();
  scanDone = 0;
  setStage('scan');
  setProgress('Getting the model ready…', 0, '', '', '');
  if (state.modelReady) {
    send({ kind: 'scan', rate: state.rate });
  } else {
    // The ORT base is resolved on the main thread: `import.meta.env.BASE_URL`
    // inside a worker resolves against the worker's own /assets/ location.
    sendEmbed({ kind: 'load', ortBase: `${import.meta.env.BASE_URL}ort/`, devices: forcedDevices() });
  }
}

/**
 * `?backend=wasm` or `?backend=webgpu` pins the inference backend.
 *
 * Not a debug flag. Asking for a graphics adapter is not reliably safe: on some
 * drivers and in some virtualised or headless browsers `requestAdapter()` never
 * settles — and it can block the thread it is called on, which puts it beyond
 * the reach of any timeout. That was reproduced during this tool's own browser
 * testing, where a scan sat at "Getting the model ready…" indefinitely.
 *
 * So there is a documented escape hatch. Somebody whose machine hangs on WebGPU
 * can append `?backend=wasm` and have a working tool instead of a dead page,
 * and the browser test can drive the WebAssembly path deterministically.
 */
function forcedDevices(): readonly Device[] | undefined {
  const asked = new URLSearchParams(location.search).get('backend');
  if (asked === 'wasm') return ['wasm'];
  if (asked === 'webgpu') return ['webgpu'];
  return undefined;
}

function setProgress(phase: string, fraction: number, count: string, rate: string, eta: string): void {
  el('scan-phase').textContent = phase;
  el('scan-count').textContent = count;
  el('scan-fill').style.width = `${Math.min(100, Math.max(0, fraction * 100)).toFixed(1)}%`;
  el('scan-rate').textContent = rate;
  el('scan-eta').textContent = eta;
}

function reportScanProgress(): void {
  const total = state.planned || 1;
  const elapsed = (performance.now() - scanStarted) / 1000;
  const rate = scanDone / Math.max(elapsed, 0.001);
  const remaining = estimateSeconds(Math.max(0, total - scanDone), rate);
  setProgress(
    'Looking at frames',
    scanDone / total,
    `${scanDone.toLocaleString()} / ${total.toLocaleString()}`,
    scanDone > 3 ? `${rate.toFixed(1)} frames/sec` : '',
    scanDone > 8 && remaining !== null ? `about ${formatDuration(remaining)} left` : '',
  );
}

function finishScan(): void {
  state.scanning = false;
  const elapsed = (performance.now() - scanStarted) / 1000;
  const count = state.index?.count ?? 0;
  logEvent('good', `Scanned ${count} frames in ${formatDuration(elapsed)} (${(count / elapsed).toFixed(1)}/sec).`);
  setStage('search');
  el('export-panel').hidden = false;
  renderExports();
  const box = el<HTMLInputElement>('query');
  box.focus();
  paintResults();
}

/* ── searching ───────────────────────────────────────────────────────── */

let searchTimer = 0;

function onQueryInput(value: string): void {
  state.query = value;
  window.clearTimeout(searchTimer);
  // The scoring pass itself is a few milliseconds, but embedding the query is a
  // model run — so the debounce protects the model, not the arithmetic.
  searchTimer = window.setTimeout(() => void runQuery(value), 220);
}

async function runQuery(text: string): Promise<void> {
  const index = state.index;
  if (!index || index.count === 0 || !text.trim()) {
    state.scores = null;
    state.moments = [];
    paintResults();
    return;
  }
  const id = ++queryId;
  const vector = await new Promise<Float32Array>((resolve) => {
    pendingQuery = resolve;
    sendEmbed({ kind: 'query', id, text });
  });
  // A slower earlier query must not overwrite a newer one's results.
  if (id !== queryId) return;

  const raw = index.score(vector);
  const smoothed = medianFilter(raw, state.settings.medianWindow);
  state.scores = smoothed;
  state.moments = findMoments(smoothed, index.timeList(), state.settings, state.facts?.duration ?? 0);
  logEvent('info', `“${text}” — ${state.moments.length} moments, best ${state.moments[0]?.peakScore.toFixed(3) ?? '—'}.`);
  requestThumbs();
  paintResults();
}

function requestThumbs(): void {
  const wanted = state.moments
    .map((m) => m.peakTime)
    .filter((t) => !state.thumbs.has(Math.round(t * 1000)));
  if (wanted.length > 0) send({ kind: 'thumbs', times: wanted, size: 240 });
}

function paintResults(): void {
  const views: ResultView[] = state.moments.map((moment) => ({
    moment,
    thumb: state.thumbs.get(Math.round(moment.peakTime * 1000)) ?? null,
  }));
  renderResults(
    el('results'),
    state.query,
    views,
    state.scores ?? new Float32Array(0),
    state.facts?.duration ?? 0,
    (moment) => void cutClip(moment),
  );
  document.getElementById('limits-link-2')?.addEventListener('click', () => openModal('What cuepoint can and cannot do', LIMITS));
  renderExports();
}

/* ── artefacts ───────────────────────────────────────────────────────── */

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  // Revoked on a turn of the event loop; revoking synchronously races the
  // click in Safari and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

async function cutClip(moment: Moment): Promise<void> {
  const facts = state.facts;
  if (!facts) return;
  const id = ++clipId;
  logEvent('info', `Cutting ${moment.start.toFixed(1)}s – ${moment.end.toFixed(1)}s…`);
  try {
    const bytes = await new Promise<ArrayBuffer>((resolve, reject) => {
      pendingClips.set(id, resolve);
      window.setTimeout(() => reject(new Error('Cutting that clip took too long.')), 120_000);
      send({ kind: 'clip', id, start: moment.start, end: moment.end });
    });
    const name = clipName(facts.name, id);
    download(new Blob([bytes], { type: 'video/mp4' }), name);
    logEvent('good', `Wrote ${name} (${(bytes.byteLength / 1e6).toFixed(1)} MB).`);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    logEvent('error', message);
    showError(message);
  }
}

function currentResults(): QueryResult[] {
  const list = [...state.results];
  if (state.query.trim() && state.moments.length > 0) {
    const existing = list.findIndex((r) => r.query === state.query);
    const entry = { query: state.query, moments: state.moments };
    if (existing >= 0) list[existing] = entry;
    else list.push(entry);
  }
  return list;
}

function renderExports(): void {
  const row = document.getElementById('export-row');
  const facts = state.facts;
  const index = state.index;
  if (!row || !facts || !index) return;
  const results = currentResults();
  const has = results.length > 0;

  row.replaceChildren();
  const button = (label: string, enabled: boolean, run: () => void) => {
    const b = document.createElement('button');
    b.className = 'btn btn-sm';
    b.type = 'button';
    b.textContent = label;
    b.disabled = !enabled;
    b.addEventListener('click', run);
    row.append(b);
  };

  button('Download cuepoints.csv', has, () =>
    download(new Blob([cuepointsCsv(results, facts)], { type: 'text/csv' }), safeName(facts.name, '-cuepoints.csv')));
  button('Download chapters.vtt', has, () =>
    download(new Blob([chaptersVtt(results)], { type: 'text/vtt' }), safeName(facts.name, '-chapters.vtt')));
  button('Save the index', index.count > 0, () => {
    const manifest = buildManifest(
      facts,
      { id: MODEL_ID, revision: MODEL_REVISION, visionDtype: state.visionDtype },
      state.rate,
      index.timeList(),
      new Date().toISOString(),
    );
    download(new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }), safeName(facts.name, '-index.json'));
    // A copy into a plain ArrayBuffer: `vectors()` is a subarray view over the
    // index's own storage, which TypeScript types as possibly shared.
    const raw = new Float32Array(index.vectors());
    download(new Blob([raw.buffer as ArrayBuffer], { type: 'application/octet-stream' }), safeName(facts.name, '-index.f32'));
  });
  button('Copy the log', true, () => void navigator.clipboard?.writeText(logText()));
}

/* ── boot ────────────────────────────────────────────────────────────── */

function bindDropzone(): void {
  const zone = el('dropzone');
  const input = el<HTMLInputElement>('file-input');

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void openFile(file);
  });

  for (const type of ['dragenter', 'dragover'] as const) {
    zone.addEventListener(type, (e) => {
      e.preventDefault();
      zone.classList.add('is-dragging');
    });
  }
  for (const type of ['dragleave', 'drop'] as const) {
    zone.addEventListener(type, () => zone.classList.remove('is-dragging'));
  }
  zone.addEventListener('drop', (e) => {
    e.preventDefault();
    const file = (e as DragEvent).dataTransfer?.files?.[0];
    if (file) void openFile(file);
  });
  // The whole page accepts a drop, because a dropzone that only works inside
  // its own dashed rectangle is a thing people miss on a first try.
  window.addEventListener('dragover', (e) => e.preventDefault());
  window.addEventListener('drop', (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && el('stage-drop').hidden === false) void openFile(file);
  });

  el('sample-btn').addEventListener('click', (e) => {
    // The pill sits next to the dropzone, not inside it, but stopping
    // propagation here costs nothing and removes a whole class of "it opened
    // the file picker as well" bug if it is ever moved.
    e.stopPropagation();
    void loadSample();
  });
}

/**
 * The one-click demo.
 *
 * Goes through `openFile`, the exact path a real upload takes — a sample with
 * its own shortcut through the pipeline is a sample that can pass while the
 * real thing is broken.
 */
async function loadSample(): Promise<void> {
  try {
    logEvent('info', 'Fetching the demo reel…');
    const res = await fetch(`${import.meta.env.BASE_URL}samples/demo-reel.mp4`);
    if (!res.ok) throw new Error(`the demo reel could not be loaded (${res.status})`);
    const blob = await res.blob();
    await openFile(new File([blob], 'demo-reel.mp4', { type: 'video/mp4' }));
  } catch (err) {
    showError(err instanceof Error ? err.message : String(err));
  }
}

function boot(): void {
  renderShell();
  initGlossary();
  bindDropzone();
  bindLogToggle(el('log-toggle'), el('log-count'));

  el('nav-how').addEventListener('click', () => openModal('How cuepoint works', HOW_IT_WORKS));
  el('nav-privacy').addEventListener('click', () => openModal('Privacy', PRIVACY));
  el('nav-about').addEventListener('click', () => openModal('About cuepoint', ABOUT));
  el('trust-badge').addEventListener('click', () => openModal('Privacy', PRIVACY));
  el('limits-link').addEventListener('click', () => openModal('What cuepoint can and cannot do', LIMITS));

  el<HTMLInputElement>('query').addEventListener('input', (e) => onQueryInput((e.target as HTMLInputElement).value));
  el('scan-cancel').addEventListener('click', () => {
    send({ kind: 'cancel' });
    logEvent('warn', 'Scan stopped. The frames already looked at are still searchable.');
  });

  document.addEventListener('keydown', (e) => {
    // `/` focuses the query box, the convention everywhere a search box is the
    // point — but not while the user is typing into something.
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !el('stage-search').hidden) {
      e.preventDefault();
      el<HTMLInputElement>('query').focus();
    }
  });

  setStage('drop');
  logEvent('info', 'Ready. Nothing has been sent anywhere.');
}

boot();
